import { initFtpManager } from './ftp-manager.js';
import {
    initFlowbiteComponents,
    setupCroppieFlow,
    destroyCroppie,
    initLegacySnippets,
    initTomSelect,
    initStickyDayNav,
} from './ui-utils.js';

/* =================================================================
   HLAVNÍ INIT FUNKCE
   Tuto funkci voláme z app.js (start) a z naja-setup.js (po ajaxu).
================================================================= */
export function initAllPageComponents(root = document) {
    initFtpManager(root);
    // console.log('🔄 --- REINIT UI START ---');

    // 1. Úklid (Clean up)
    // Musíme zničit staré instance, aby se nehromadily v paměti.
    destroyCroppie(root);

    // 2. Základní knihovny
    initLegacySnippets(root);
    initFlowbiteComponents(root);

    // 3. Specifické prvky
    initThemeToggle(root);
    initHamburger(root);
    initClipboardButtons(root);
    initTomSelect([
        "#frm-menuItemForm-meal_id", // select v jídleníčku
        "#frm-addItemForm-menu_day_item_id", // select v objednávce
        "#frm-createOrderForm-user_id", // select v objednávce
        "#zone" // Oblast rozvozu v registraci
    ]);
    initStickyDayNav(root);

    // 4. Croppie (načte se jen pokud existují prvky)
    setupCroppieFlow(root);



    // console.log('✅ --- REINIT UI DONE ---');
}

/* =================================================================
   SPECIFICKÉ UI KOMPONENTY
================================================================= */

/** * Tmavý režim (Dark Mode)
 * Zahrnuje ukládání do localStorage a přepínání ikonek.
 */
function initThemeToggle(root) {
    const html = document.documentElement;
    let toggleBtn = root.querySelector('#theme-toggle') || document.querySelector('#theme-toggle');

    if (!toggleBtn) return;
    if (toggleBtn.dataset.listenerAttached) return;

    const darkIcon = document.querySelector('#theme-toggle-dark-icon');
    const lightIcon = document.querySelector('#theme-toggle-light-icon');
    const tooltipTheme = document.querySelector('#tooltip-theme');

    const updateThemeIcons = () => {
        // Ikonky nastavíme podle toho, jakou třídu má HTML teď (což nastavil script v hlavičce)
        if (html.classList.contains('dark')) {
            darkIcon?.classList.add('hidden');
            lightIcon?.classList.remove('hidden');
        } else {
            darkIcon?.classList.remove('hidden');
            lightIcon?.classList.add('hidden');
        }
    };

    // Logika přepnutí (Click)
    toggleBtn.addEventListener('click', () => {
        html.classList.toggle('dark');
        // Ukládáme 'theme'
        localStorage.setItem('theme', html.classList.contains('dark') ? 'dark' : 'light');
        updateThemeIcons();
    });

    // Tooltipy
    ['mouseenter','focus'].forEach(e => toggleBtn.addEventListener(e, () => tooltipTheme?.classList.remove('invisible','opacity-0')));
    ['mouseleave','blur'].forEach(e => toggleBtn.addEventListener(e, () => tooltipTheme?.classList.add('invisible','opacity-0')));

    toggleBtn.dataset.listenerAttached = 'true';
    toggleBtn.classList.remove('hidden');

    // === ZMĚNA ZDE ===
    // Smazali jsme tu logiku "Prvotní nastavení", která znovu sahala na localStorage a html.classList
    // Místo toho jen aktualizujeme ikonky podle aktuálního stavu.
    updateThemeIcons();
}

/** * Hamburger Menu (Mobile)
 * Sleduje změny v DOMu (MutationObserver), aby poznalo, kdy se otevře drawer.
 */
function initHamburger(root) {
    // Pokud už observer běží, nic neděláme (běží globálně pro celou session)
    if (window.hamburgerObserverInitialized) return;

    const btn = document.querySelector('#toggle-hamburger-btn');
    if (!btn) return;

    const hamburger = document.querySelector('#toggle-hamburger-btn-open');
    const close = document.querySelector('#toggle-hamburger-btn-close');

    const setOpen = () => { hamburger?.classList.add('hidden'); close?.classList.remove('hidden'); };
    const setClosed = () => { hamburger?.classList.remove('hidden'); close?.classList.add('hidden'); };

    // Sledujeme, jestli Flowbite přidal backdrop (tmavé pozadí), což značí otevřené menu
    new MutationObserver(mutations => {
        mutations.forEach(m => {
            m.addedNodes.forEach(n => { if (n.nodeType === 1 && n.hasAttribute?.('drawer-backdrop')) setOpen(); });
            m.removedNodes.forEach(n => { if (n.nodeType === 1 && n.hasAttribute?.('drawer-backdrop')) setClosed(); });
        });
    }).observe(document.body, { childList: true, subtree: true });

    window.hamburgerObserverInitialized = true;
    setClosed();
}

/** * Kopírování do schránky (Clipboard)
 */
function initClipboardButtons(root) {
    const buttons = root.querySelectorAll('.copy-element-inside .copy-btn');

    buttons.forEach(btn => {
        if (btn.dataset.clipboardInitialized) return; // Ochrana proti duplicitě
        btn.dataset.clipboardInitialized = 'true';

        btn.addEventListener('click', function() {
            const val = this.parentNode.querySelector('.copy-value')?.value || this.parentNode.querySelector('.copy-value')?.textContent || '';
            const icon = this.querySelector('i');

            // Vizuální efekt (fajfka)
            const animateSuccess = () => {
                icon.classList.remove('fa-clipboard');
                icon.classList.add('fa-check');
                setTimeout(() => {
                    icon.classList.remove('fa-check');
                    icon.classList.add('fa-clipboard');
                }, 2000);
            };

            // Zápis do schránky
            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(val).then(animateSuccess);
            } else {
                // Fallback pro starší prohlížeče
                const t = document.createElement('textarea');
                t.value = val;
                document.body.appendChild(t);
                t.select();
                document.execCommand('copy');
                document.body.removeChild(t);
                animateSuccess();
            }
        });
    });
}

export function initLazyLoads() {
    const lazyBlocks = document.querySelectorAll('.js-lazy-load');

    lazyBlocks.forEach(block => {
        const url = block.dataset.src;
        if (!url) return;

        // Použijeme fetch (univerzální) nebo naja.makeRequest
        fetch(url, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
        })
            .then(response => response.text())
            .then(html => {
                block.innerHTML = html;
                block.classList.remove('animate-pulse');
                // Pokud je potřeba, reinicializujeme komponenty uvnitř (Flowbite atd.)
                // initAllPageComponents(block);
            })
            .catch(err => console.error('Chyba načítání bloku:', err));
    });
}